import React from "react";
import { IForm } from "../../../../types/form.type";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { useAlertContext } from "../../../../contexts/alert/AlertProvider";
import { Controller, useForm } from "react-hook-form";
import { asyncPostData, asyncPutData } from "../../../../utils/httpRequests";
import ModalForm from "../../modal/ModalForm";
import { Box, Grid2, Stack } from "@mui/material";
import SelectApiInput from "../../input/SelectApiInput";
import TextInput from "../../input/TextInput";
import { generateRandomString, numeralCustom } from "../../../../utils/helpers";
import moment from "moment";
import DateInput from "../../input/DateInput";

const schema = yup.object({
  ten_chietkhau: yup.string().required("Vui lòng nhập tên chiết khấu"),
  hieu_luc_tu: yup
    .date()
    .typeError("Vui lòng chọn ngày bắt đầu")
    .required("Vui lòng chọn ngày bắt đầu"),
  hieu_luc_den: yup
    .date()
    .typeError("Vui lòng chọn ngày kết thúc")
    .required("Vui lòng chọn ngày kết thúc"),
});

function FormDmChietKhau({
  defaultValues,
  isEdit,
  apiCode,
  open,
  onClose,
  setLoad,
}: IForm) {
  const app = useSelector((state: any) => state.app.data);
  const token = useSelector((state: any) => state.auth.token);
  const { showAlert } = useAlertContext();
  const defaultData: { [key: string]: any } = {
    ma_chietkhau: "",
    ten_chietkhau: "",
    hieu_luc_tu: moment().format("YYYY-MM-DD"),
    hieu_luc_den: moment().add(30, "days").format("YYYY-MM-DD"),
    vat_tu: null,
    nhom_vat_tu: null,
    ty_le_ck: 0,
    tien_ck: 0,
    sl_tu: 0,
    sl_den: 0,
  };
  const originData = defaultValues
    ? {
        ...defaultData,
        ...defaultValues,
        vat_tu: defaultValues?.ma_vt
          ? { ma_vt: defaultValues.ma_vt, ten_vt: defaultValues.ten_vt }
          : null,
        nhom_vat_tu: defaultValues?.ma_nvt
          ? { ma_nvt: defaultValues.ma_nvt, ten_nvt: defaultValues.ten_nvt }
          : null,
      }
    : defaultData;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: originData,
    resolver: yupResolver(schema),
  });

  const handleReset = () => {
    reset(originData);
  };

  const generateDataPost = async (values: typeof defaultData) => {
    const { vat_tu, nhom_vat_tu, ma_chietkhau, ...fields } = values;
    return {
      ...fields,
      ma_chietkhau: ma_chietkhau || generateRandomString(8),
      ma_vt: vat_tu?.ma_vt || "",
      ma_nvt: nhom_vat_tu?._id || "",
    };
  };

  const handleSave = async (values: typeof defaultData) => {
    const dataPost: { [key: string]: any } = await generateDataPost(values);
    let resp = null;
    if (isEdit) {
      resp = await asyncPutData({
        id: dataPost._id,
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    } else {
      resp = await asyncPostData({
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    }
    if (resp.status !== 200) {
      showAlert({
        type: "error",
        message:
          resp?.data?.error || resp?.data?.message || "Lỗi khi lưu đối tượng",
      });
    } else {
      onClose();
      setLoad?.((prev: number) => prev + 1);
    }
  };

  const onSubmit = handleSubmit(handleSave);

  return (
    <ModalForm
      formWidth="800px"
      formTitle="chiết khấu"
      loading={isSubmitting}
      open={open}
      isEdit={isEdit}
      onClose={onClose}
      onSubmit={onSubmit}
      onReset={handleReset}
    >
      <Box>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 6 }}>
            <Stack gap={2}>
              <TextInput
                readOnly={isEdit}
                label="Mã CK"
                labelWidth="25%"
                placeholder="Nhập hoặc tạo tự động"
                name="ma_chietkhau"
                register={register}
              />
              <Controller
                name="hieu_luc_tu"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <DateInput
                      label="Hiệu lực từ"
                      labelWidth="25%"
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
              <Controller
                name="vat_tu"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <SelectApiInput
                      labelWidth="25%"
                      label="Sản phẩm"
                      placeholder="Chọn sản phẩm"
                      apiCode="dmvt"
                      searchFields={["ma_vt", "ten_vt"]}
                      getOptionLabel={(option) => option.ten_vt}
                      checkEqual={(value, option) =>
                        value?.ma_vt === option?.ma_vt
                      }
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
              <Controller
                name="sl_tu"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      label="SL áp dụng"
                      labelWidth="25%"
                      value={numeralCustom(value).format()}
                      onChange={(e) => {
                        const val = numeralCustom(e.target.value).value();
                        onChange(val);
                      }}
                    />
                  );
                }}
              />
            </Stack>
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <Stack gap={2}>
              <TextInput
                required
                label="Tên CK"
                labelWidth="25%"
                placeholder="Nhập tên chiết khấu"
                name="ten_chietkhau"
                register={register}
                errorMessage={errors?.ten_chietkhau?.message as string}
              />

              <Controller
                name="hieu_luc_den"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <DateInput
                      label="Hiệu lực đến"
                      labelWidth="25%"
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
              <Controller
                name="tien_ck"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      label="Tiền CK"
                      labelWidth="25%"
                      endAdornment="VND"
                      value={numeralCustom(value).format()}
                      onChange={(e) => {
                        const newTienCk = numeralCustom(e.target.value).value();
                        onChange(newTienCk);
                      }}
                    />
                  );
                }}
              />
            </Stack>
          </Grid2>
        </Grid2>
      </Box>
    </ModalForm>
  );
}

export default FormDmChietKhau;
